import { useEffect, useRef } from 'react'
import { useFilterDimsState } from './useFilterDimsState'
import { useValueLabels } from '~/api/hooks/useCatalog'

/**
 * On first mount of a chart page, if URL has no dim filters set, apply
 * is_default-flagged values from v_value_labels for the given factTable.
 *
 * `factTable` is the REGISTRY-resolved active fact, not the static per-page
 * prop (they differ on ReviewKpis: static media_national vs registry
 * kpi_overall). Pass `null` while the registry is unsettled — a null fact
 * disables the underlying query, so the seed cannot fire against the
 * transient static fallback and then get locked in by the ref-guard below.
 *
 * Implementation notes:
 * - The `applied.current` ref-guard is load-bearing for the user-cleared-
 *   defaults case: without it, defaults would re-apply after the user
 *   explicitly cleared them via the chip selectors. The guard ensures
 *   defaults are applied at most once per mount.
 * - The null→active-fact transition is safe: a null fact yields no data, so
 *   the effect early-returns WITHOUT tripping the guard, and the seed only
 *   fires once the resolved fact's labels arrive. A later fact SWITCH
 *   (active → different active) does NOT re-seed — the guard is once-per-
 *   mount by design; per-active-fact re-seeding is the fact-switch task's job
 *   and needs a `useEffect(() => { applied.current = false }, [factTable])`
 *   reset (and a re-design of the guard semantics).
 */
export function useDimDefaults(factTable: string | null) {
  const [filters, setFilters] = useFilterDimsState()
  const valueLabels = useValueLabels(factTable)
  const applied = useRef(false)

  useEffect(() => {
    if (applied.current) return
    if (!valueLabels.data) return
    if (Object.keys(filters).length > 0) {
      applied.current = true
      return
    }
    const defaults: Record<string, string[]> = {}
    for (const r of valueLabels.data) {
      if (r.is_default) {
        defaults[r.dim_name] = [...(defaults[r.dim_name] ?? []), r.value]
      }
    }
    if (Object.keys(defaults).length > 0) setFilters(defaults, { replace: true })
    applied.current = true
  }, [valueLabels.data, filters, setFilters])
}
